import { useEffect, useState } from "react";
import { api, fmtDate, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerTrigger } from "@/components/ui/drawer";
import { Plus, CalendarCheck, Plane } from "lucide-react";
import { toast } from "sonner";

export default function StaffAttendance() {
  const [att, setAtt] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "leave", from_date: "", to_date: "", reason: "" });

  const load = () => {
    api.get("/attendance?days=30").then((r) => setAtt(r.data));
    api.get("/leave-requests").then((r) => setLeaves(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/leave-requests", form);
      toast.success("Đã gửi đơn"); setOpen(false); setForm({ type: "leave", from_date: "", to_date: "", reason: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lỗi"); }
  };

  return (
    <div className="p-4 space-y-3" data-testid="staff-attendance">
      <h2 className="font-serif text-2xl font-bold flex items-center gap-2"><CalendarCheck className="w-6 h-6 text-emerald-800" />Chấm công</h2>

      <div className="bg-white border border-stone-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Đơn từ của tôi</h3>
          <Drawer open={open} onOpenChange={setOpen}>
            <DrawerTrigger asChild>
              <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-create-leave"><Plus className="w-4 h-4 mr-1" />Gửi đơn</Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="text-left">
                <DrawerTitle className="font-serif text-xl flex items-center gap-2"><Plane className="w-5 h-5 text-emerald-800" />Gửi đơn nghỉ / OT</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 space-y-3">
                <div>
                  <Label className="text-xs">Loại đơn</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="h-11 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="leave">Nghỉ phép</SelectItem>
                      <SelectItem value="ot">Làm thêm OT</SelectItem>
                      <SelectItem value="shift_swap">Đổi ca</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Từ ngày</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="h-11 rounded-sm mt-1" /></div>
                  <div><Label className="text-xs">Đến ngày</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="h-11 rounded-sm mt-1" /></div>
                </div>
                <div>
                  <Label className="text-xs">Lý do</Label>
                  <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} className="rounded-sm mt-1" />
                </div>
              </div>
              <DrawerFooter>
                <Button onClick={submit} className="h-12 bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-submit-leave">Gửi đơn</Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
        <div className="space-y-2">
          {leaves.map((l) => (
            <div key={l.id} className="border border-stone-200 rounded-sm p-2.5">
              <div className="flex items-center justify-between mb-0.5">
                <div className="text-sm font-semibold">{l.type}</div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${
                  l.status === "approved" ? "bg-emerald-100 text-emerald-900" :
                  l.status === "rejected" ? "bg-red-100 text-red-900" :
                  "bg-amber-100 text-amber-900"}`}>{l.status}</span>
              </div>
              <div className="text-xs text-stone-600">{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</div>
              {l.reason && <div className="text-xs text-stone-500 mt-1">{l.reason}</div>}
            </div>
          ))}
          {leaves.length === 0 && <div className="text-center text-stone-400 text-sm py-4">Chưa có đơn</div>}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm p-4">
        <h3 className="font-semibold mb-2">Lịch sử chấm công (30 ngày)</h3>
        <div className="space-y-1.5">
          {att.slice(0, 30).map((a) => (
            <div key={a.id} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0">
              <div>
                <div className="text-sm font-medium">{fmtDate(a.work_date)}</div>
                <div className="text-[10px] text-stone-500 font-mono">{fmtDateTime(a.check_in_at)?.split(",")[1]} → {fmtDateTime(a.check_out_at)?.split(",")[1] || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-semibold">{Math.round((a.work_minutes || 0) / 60 * 10) / 10}h</div>
                <span className={`text-[10px] px-1 py-0.5 rounded-sm font-semibold ${a.status === "late" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"}`}>{a.status}</span>
              </div>
            </div>
          ))}
          {att.length === 0 && <div className="text-center text-stone-400 text-sm py-4">Chưa có dữ liệu</div>}
        </div>
      </div>
    </div>
  );
}
